// ============================================================
// WebsiteView — the "Sito web" tab. Two-pane editor: a sticky section nav
// (scroll-spy) on the left, the content cards on the right. Lets the owner
// edit their public website's CONTENT — never its design/structure (locked
// in the repo config). Debounced auto-save to draft; "Pubblica" promotes it.
// Photos upload to Spaces (downscaled client-side) when configured; otherwise
// owners paste image URLs.
// ============================================================
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import './WebsiteView.css';
import type { Lang } from '../data/types';
import { useDashboard, useDashboardActions } from '../data/context';
import { downscaleImage } from '../lib/image';
import type { SiteContent, SiteMenuTag, SiteWeekday } from '@risma/data-client';

interface Props {
  lang: Lang;
}

const DAYS: { key: SiteWeekday; label: string }[] = [
  { key: 'mon', label: 'Lunedì' },
  { key: 'tue', label: 'Martedì' },
  { key: 'wed', label: 'Mercoledì' },
  { key: 'thu', label: 'Giovedì' },
  { key: 'fri', label: 'Venerdì' },
  { key: 'sat', label: 'Sabato' },
  { key: 'sun', label: 'Domenica' },
];

const TAGS: { key: SiteMenuTag; label: string }[] = [
  { key: 'vegetariano', label: 'Vegetariano' },
  { key: 'vegano', label: 'Vegano' },
  { key: 'piccante', label: 'Piccante' },
  { key: 'senza-glutine', label: 'Senza glutine' },
  { key: 'senza-lattosio', label: 'Senza lattosio' },
  { key: 'specialita', label: 'Specialità' },
  { key: 'novita', label: 'Novità' },
  { key: 'crudo', label: 'Crudo' },
];

const SECTIONS: { id: string; label: string; icon: SecName }[] = [
  { id: 'hero', label: 'Hero', icon: 'star' },
  { id: 'storia', label: 'Identità & storia', icon: 'book' },
  { id: 'menu', label: 'Menù', icon: 'grid' },
  { id: 'orari', label: 'Orari', icon: 'clock' },
  { id: 'galleria', label: 'Galleria', icon: 'image' },
  { id: 'contatti', label: 'Contatti', icon: 'mail' },
  { id: 'social', label: 'Social', icon: 'share' },
  { id: 'seo', label: 'Google & SEO', icon: 'search' },
];

type Status = 'loading' | 'saved' | 'dirty' | 'saving' | 'error' | 'publishing' | 'published';

export function WebsiteView(_props: Props) {
  const actions = useDashboardActions();
  const { isImpersonated, tenant } = useDashboard();
  const [content, setContent] = useState<SiteContent>({});
  const [status, setStatus] = useState<Status>('loading');
  const [active, setActive] = useState<string>('hero');
  const [published, setPublished] = useState(false);
  const [uploadsEnabled, setUploadsEnabled] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Downscale then upload a photo; resolves to its public URL. */
  async function uploadPhoto(file: File): Promise<string> {
    const blob = await downscaleImage(file);
    return actions.uploadImage(blob);
  }

  // ── live preview ──
  // In impersonazione il portale è quello OSPITE (Carlotta): l'anteprima parte
  // dal suo sito. Chiediamo la modalità NEUTRA (?neutral=1, contenuti vuoti) +
  // lo STILE del cliente (carattere/colori/hero/numeri scelti in Console), così
  // l'anteprima rispecchia il look reale e non quello di default di Carlotta.
  const siteBase = import.meta.env.VITE_SITE_URL;
  const previewUrl = (() => {
    if (!siteBase) return null;
    const p = new URLSearchParams();
    if (isImpersonated) p.set('neutral', '1');
    if (tenant?.designStyle) p.set('style', tenant.designStyle);
    if (tenant?.heroLayout) p.set('hero', tenant.heroLayout);
    if (tenant?.sectionNumerals != null) p.set('numerals', tenant.sectionNumerals ? 'on' : 'off');
    const qs = p.toString();
    return `${siteBase.replace(/\/$/, '')}/preview${qs ? `?${qs}` : ''}`;
  })();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const contentRef = useRef<SiteContent>({});
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Full-tab previews opened via "Apri ↗". We keep their window handles so
  // they receive the same live updates as the embedded iframe.
  const popupRefs = useRef<Set<Window>>(new Set());

  function postTo(target: Window | null | undefined, c: SiteContent) {
    if (!siteBase || !target) return;
    try {
      // `name` non è contenuto editabile (sta sul tenant, non in SiteContent):
      // lo mandiamo a parte così l'anteprima — anche quella neutra — mostra il
      // nome VERO del locale invece del segnaposto.
      target.postMessage({ source: 'risma-editor', content: c, name: tenant?.name }, siteBase);
    } catch {
      /* window gone */
    }
  }
  function postPreview(c: SiteContent) {
    if (!siteBase) return;
    postTo(iframeRef.current?.contentWindow, c);
    for (const w of popupRefs.current) {
      if (w.closed) popupRefs.current.delete(w);
      else postTo(w, c);
    }
  }
  function schedulePreview(c: SiteContent) {
    contentRef.current = c;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => postPreview(c), 180);
  }
  function openFullPreview() {
    if (!previewUrl) return;
    // Named target → reuses the same tab on repeat clicks instead of piling up.
    const w = window.open(previewUrl, 'risma-preview-full');
    if (w) {
      popupRefs.current.add(w);
      w.focus();
    }
  }

  // ── first-load prefill ──
  // A client who has never edited should still open with every field filled
  // with the content Risma built the site from — not a blank form. The preview
  // hands us that base content; we adopt it (and persist it as the draft) the
  // first time, but ONLY when the DB draft was empty (so we never clobber real
  // edits a returning owner has saved).
  //
  // ECCEZIONE — sessione impersonata (staff dalla Console): un cliente nuovo,
  // prima del provisioning, non ha ancora un suo portale, quindi l'editor passa
  // dal portale OSPITE (oggi quello di Carlotta) e la sua anteprima è di un ALTRO
  // tenant. Adottarne il baseContent travaserebbe i contenuti di Carlotta nel
  // cliente nuovo. Quindi: impersonato → niente prefill, si parte puliti.
  const needPrefill = useRef(false);
  const baseContentRef = useRef<SiteContent | null>(null);
  function tryPrefill() {
    if (!needPrefill.current || !baseContentRef.current) return;
    needPrefill.current = false;
    const base = structuredClone(baseContentRef.current);
    setContent(base);
    contentRef.current = base;
    postPreview(base);
    // Persist once so the starting content is durable from day one.
    setStatus('saving');
    actions.saveSiteDraft(base).then(() => setStatus('saved')).catch(() => setStatus('error'));
  }

  useEffect(() => {
    let alive = true;
    actions
      .loadSite()
      .then((s) => {
        if (!alive) return;
        const draft = s.draft ?? {};
        setContent(draft);
        contentRef.current = draft;
        setPublished(!!s.publishedAt);
        setUploadsEnabled(!!s.uploadsEnabled);
        setStatus('saved');
        // Empty draft → flag for prefill; runs now if base content already
        // arrived from the preview, otherwise when its ready message lands.
        // Mai in impersonazione: l'anteprima ospite è di un altro tenant.
        if (Object.keys(draft).length === 0 && !isImpersonated) {
          needPrefill.current = true;
          tryPrefill();
        }
      })
      .catch(() => alive && setStatus('error'));
    return () => {
      alive = false;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a preview surface (iframe OR full-tab) signals it's ready, send the
  // current content straight back to that specific window so it syncs at once.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (siteBase && e.origin === siteBase && e.data?.source === 'risma-preview' && e.data.ready) {
        // Capture the base content (first surface to report wins) and prefill
        // if the editor was waiting on it.
        if (e.data.baseContent && !baseContentRef.current) {
          baseContentRef.current = e.data.baseContent as SiteContent;
          tryPrefill();
        }
        postTo((e.source as Window) ?? iframeRef.current?.contentWindow, contentRef.current);
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll-spy: highlight the section nearest the top of the scroll area.
  useEffect(() => {
    if (status === 'loading') return;
    const els = SECTIONS.map((s) => document.getElementById('we-sec-' + s.id)).filter(Boolean) as HTMLElement[];
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id.replace('we-sec-', ''));
      },
      { rootMargin: '-80px 0px -65% 0px', threshold: 0 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [status]);

  function edit(producer: (c: SiteContent) => void) {
    setContent((prev) => {
      const next: SiteContent = structuredClone(prev);
      producer(next);
      scheduleSave(next);
      schedulePreview(next);
      return next;
    });
  }
  function scheduleSave(next: SiteContent) {
    setStatus('dirty');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setStatus('saving');
      actions.saveSiteDraft(next).then(() => setStatus('saved')).catch(() => setStatus('error'));
    }, 800);
  }
  async function publish() {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setStatus('saving');
    try {
      await actions.saveSiteDraft(content);
      setStatus('publishing');
      await actions.publishSite();
      setPublished(true);
      setStatus('published');
      setTimeout(() => setStatus('saved'), 2500);
    } catch {
      setStatus('error');
    }
  }
  function jump(id: string) {
    document.getElementById('we-sec-' + id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const id = content.identity ?? {};
  const hero = content.hero ?? {};
  const menu = content.menu ?? { lists: [] };
  const hours = content.hours ?? { timezone: 'Europe/Rome', week: [] };
  const gallery = content.gallery ?? { images: [] };
  const contact = content.contact ?? {};
  const social = content.social ?? {};
  const seo = content.seo ?? {};
  const hoursDay = (day: SiteWeekday) => hours.week.find((d) => d.day === day) ?? { day, ranges: [] as { open: string; close: string }[] };

  const statusLabel: Record<Status, string> = {
    loading: 'Caricamento…',
    saved: 'Salvato',
    dirty: 'Modifiche non salvate',
    saving: 'Salvataggio…',
    error: 'Errore',
    publishing: 'Pubblicazione…',
    published: 'Pubblicato',
  };

  if (status === 'loading') {
    return <div className="view we-view"><p className="we-muted" style={{ padding: 24 }}>Caricamento del sito…</p></div>;
  }

  return (
    <div className="view we-view">
      {/* ── slim action bar (title lives in the topbar) ── */}
      <div className="we-head">
        <div className="we-head-l">
          {published ? <span className="we-live">● Online</span> : <span className="we-draft">● Bozza</span>}
        </div>
        <div className="we-head-r">
          <span className={'we-savechip we-savechip--' + status}>{statusLabel[status]}</span>
          <button className="we-btn we-btn-primary" onClick={() => void publish()} disabled={status === 'publishing' || status === 'saving'}>
            Pubblica
          </button>
        </div>
      </div>

      <div className={'we-shell' + (previewUrl ? ' we-shell--preview' : '')}>
        {/* ── section nav ── */}
        <nav className="we-nav" aria-label="Sezioni">
          <p className="we-nav-title">Sezioni</p>
          {SECTIONS.map((s) => (
            <button key={s.id} title={s.label} className={'we-nav-item' + (active === s.id ? ' active' : '')} onClick={() => jump(s.id)}>
              <SecIcon name={s.icon} /> <span>{s.label}</span>
            </button>
          ))}
        </nav>

        {/* ── content ── */}
        <div className="we-main">
          <Section id="hero" icon="star" title="Hero (testata)" desc="La prima cosa che vede chi arriva sul sito.">
            <Field label="Sottotitolo" hint="La frase sotto al nome del locale.">
              <textarea className="we-input we-textarea" rows={2} value={hero.subhead ?? ''} placeholder="La frase che racconta il tuo locale…"
                onChange={(e) => edit((c) => { (c.hero ??= {}).subhead = e.target.value || undefined; })} />
            </Field>
            <ImageField label="Foto hero" hint="La grande foto in cima alla pagina." img={hero.image}
              canUpload={uploadsEnabled} upload={uploadPhoto}
              onChange={(img) => edit((c) => { (c.hero ??= {}).image = img; })} />
          </Section>

          <Section id="storia" icon="book" title="Identità & storia" desc="Il claim, l'anno e il racconto del locale.">
            <Field label="Slogan (tagline)">
              <input className="we-input" value={id.tagline ?? ''} placeholder="La tua frase di benvenuto"
                onChange={(e) => edit((c) => { (c.identity ??= {}).tagline = e.target.value || undefined; })} />
            </Field>
            <Row>
              <Field label="Anno di fondazione">
                <input className="we-input" type="number" value={id.foundedYear ?? ''} placeholder="es. 1985"
                  onChange={(e) => edit((c) => { (c.identity ??= {}).foundedYear = e.target.value ? Number(e.target.value) : undefined; })} />
              </Field>
              <Field label="Zona / quartiere">
                <input className="we-input" value={id.locality ?? ''} placeholder="Il tuo quartiere o zona"
                  onChange={(e) => edit((c) => { (c.identity ??= {}).locality = e.target.value || undefined; })} />
              </Field>
            </Row>
            <Row>
              <Field label="Titolo storia">
                <input className="we-input" value={id.storyHeading ?? ''} placeholder="Il titolo della tua storia"
                  onChange={(e) => edit((c) => { (c.identity ??= {}).storyHeading = e.target.value || undefined; })} />
              </Field>
              <Field label="Parola in evidenza" hint="Mostrata in corsivo/colore.">
                <input className="we-input" value={id.storyEmphasis ?? ''} placeholder="parola in evidenza"
                  onChange={(e) => edit((c) => { (c.identity ??= {}).storyEmphasis = e.target.value || undefined; })} />
              </Field>
            </Row>
            <Field label="Paragrafi della storia">
              {(id.story ?? []).map((p, i) => (
                <div className="we-listrow" key={i}>
                  <span className="we-grip" aria-hidden="true">⠿</span>
                  <textarea className="we-input we-textarea" value={p} rows={2}
                    onChange={(e) => edit((c) => { (c.identity ??= {}).story![i] = e.target.value; })} />
                  <button className="we-link-rm" onClick={() => edit((c) => { c.identity!.story!.splice(i, 1); if (!c.identity!.story!.length) delete c.identity!.story; })}>Rimuovi</button>
                </div>
              ))}
              <button className="we-addline" onClick={() => edit((c) => { ((c.identity ??= {}).story ??= []).push(''); })}>+ Aggiungi paragrafo</button>
            </Field>
            <Field label="Citazione" hint="Mostrata solo se non c'è una foto storia.">
              <input className="we-input" value={id.storyPull ?? ''} placeholder="Una frase che vi rappresenta."
                onChange={(e) => edit((c) => { (c.identity ??= {}).storyPull = e.target.value || undefined; })} />
            </Field>
            <ImageField label="Foto storia" img={id.storyImage}
              canUpload={uploadsEnabled} upload={uploadPhoto}
              onChange={(img) => edit((c) => { (c.identity ??= {}).storyImage = img; })} />
          </Section>

          <Section id="menu" icon="grid" title="Menù" desc="Una o più carte, con categorie e piatti."
            action={<button className="we-link-add" onClick={() => edit((c) => { (c.menu ??= { lists: [] }).lists.push({ name: 'Menù', categories: [] }); })}>+ Aggiungi carta</button>}>
            {menu.lists.length === 0 && <p className="we-empty">Nessuna carta. Aggiungine una per iniziare.</p>}
            {menu.lists.map((list, li) => (
              <div className="we-block" key={li}>
                <div className="we-block-head">
                  <span className="we-grip" aria-hidden="true">⠿</span>
                  <input className="we-input we-strong" value={list.name} placeholder="Alla carta"
                    onChange={(e) => edit((c) => { c.menu!.lists[li].name = e.target.value; })} />
                  <button className="we-link-rm" onClick={() => edit((c) => { c.menu!.lists.splice(li, 1); })}>Rimuovi</button>
                </div>
                <input className="we-input we-sub" value={list.description ?? ''} placeholder="Descrizione (facoltativa)"
                  onChange={(e) => edit((c) => { c.menu!.lists[li].description = e.target.value || undefined; })} />
                {(list.categories ?? []).map((cat, ci) => (
                  <div className="we-block we-block--cat" key={ci}>
                    <div className="we-block-head">
                      <input className="we-input we-cat" value={cat.name} placeholder="Antipasti"
                        onChange={(e) => edit((c) => { c.menu!.lists[li].categories![ci].name = e.target.value; })} />
                      <button className="we-link-rm" onClick={() => edit((c) => { c.menu!.lists[li].categories!.splice(ci, 1); })}>Rimuovi</button>
                    </div>
                    {cat.items.map((item, ii) => (
                      <div className="we-dish" key={ii}>
                        <div className="we-dish-top">
                          <input className="we-input" value={item.name} placeholder="Nome del piatto"
                            onChange={(e) => edit((c) => { c.menu!.lists[li].categories![ci].items[ii].name = e.target.value; })} />
                          <div className="we-price-wrap"><span className="we-eur">€</span>
                            <input className="we-input we-price" type="number" value={item.price ?? ''} placeholder="0"
                              onChange={(e) => edit((c) => { c.menu!.lists[li].categories![ci].items[ii].price = e.target.value ? Number(e.target.value) : undefined; })} /></div>
                          <button className="we-icon-rm" title="Rimuovi piatto" onClick={() => edit((c) => { c.menu!.lists[li].categories![ci].items.splice(ii, 1); })}>✕</button>
                        </div>
                        <input className="we-input we-sub" value={item.description ?? ''} placeholder="Descrizione"
                          onChange={(e) => edit((c) => { c.menu!.lists[li].categories![ci].items[ii].description = e.target.value || undefined; })} />
                        <DishPhoto img={item.image} canUpload={uploadsEnabled} upload={uploadPhoto}
                          onChange={(img) => edit((c) => { c.menu!.lists[li].categories![ci].items[ii].image = img; })} />
                        <div className="we-tags">
                          {TAGS.map((tg) => {
                            const on = (item.tags ?? []).includes(tg.key);
                            return (
                              <button key={tg.key} className={'we-chip' + (on ? ' on' : '')}
                                onClick={() => edit((c) => {
                                  const it = c.menu!.lists[li].categories![ci].items[ii];
                                  const set = new Set(it.tags ?? []);
                                  if (set.has(tg.key)) set.delete(tg.key); else set.add(tg.key);
                                  it.tags = set.size ? [...set] : undefined;
                                })}>{tg.label}</button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    <button className="we-addline" onClick={() => edit((c) => { c.menu!.lists[li].categories![ci].items.push({ name: '' }); })}>+ Piatto</button>
                  </div>
                ))}
                <button className="we-addline" onClick={() => edit((c) => { (c.menu!.lists[li].categories ??= []).push({ name: '', items: [] }); })}>+ Categoria</button>
              </div>
            ))}
            <Field label="Nota a piè di menù">
              <input className="we-input" value={menu.note ?? ''} placeholder="es. Coperto €2 · menù del giorno"
                onChange={(e) => edit((c) => { (c.menu ??= { lists: [] }).note = e.target.value || undefined; })} />
            </Field>
          </Section>

          <Section id="orari" icon="clock" title="Orari" desc="Apertura per ogni giorno. Lascia chiuso dove serve.">
            <div className="we-hours">
              {DAYS.map((d) => {
                const day = hoursDay(d.key);
                return (
                  <div className="we-day" key={d.key}>
                    <span className="we-day-name">{d.label}</span>
                    <label className="we-closed">
                      <input type="checkbox" checked={!!day.closed}
                        onChange={(e) => edit((c) => {
                          const h = (c.hours ??= { timezone: 'Europe/Rome', week: [] });
                          let row = h.week.find((x) => x.day === d.key);
                          if (!row) { row = { day: d.key }; h.week.push(row); }
                          row.closed = e.target.checked || undefined;
                          if (e.target.checked) row.ranges = undefined;
                        })} /> Chiuso
                    </label>
                    {!day.closed && (
                      <div className="we-ranges">
                        {(day.ranges ?? []).map((r, ri) => (
                          <span className="we-range" key={ri}>
                            <input className="we-input we-time" value={r.open} placeholder="12:00"
                              onChange={(e) => edit((c) => { ensureDay(c, d.key).ranges![ri].open = e.target.value; })} />
                            <span className="we-dash">–</span>
                            <input className="we-input we-time" value={r.close} placeholder="15:00"
                              onChange={(e) => edit((c) => { ensureDay(c, d.key).ranges![ri].close = e.target.value; })} />
                            <button className="we-icon-rm" onClick={() => edit((c) => { ensureDay(c, d.key).ranges!.splice(ri, 1); })}>✕</button>
                          </span>
                        ))}
                        <button className="we-addline" onClick={() => edit((c) => { const row = ensureDay(c, d.key); (row.ranges ??= []).push({ open: '19:00', close: '23:00' }); row.closed = undefined; })}>+ Fascia</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <Field label="Nota orari">
              <input className="we-input" value={hours.specialNote ?? ''} placeholder="Chiuso dal 10 al 24 agosto."
                onChange={(e) => edit((c) => { (c.hours ??= { timezone: 'Europe/Rome', week: [] }).specialNote = e.target.value || undefined; })} />
            </Field>
          </Section>

          <Section id="galleria" icon="image" title="Galleria" desc="Le foto in fondo alla pagina."
            action={<button className="we-link-add" onClick={() => edit((c) => { (c.gallery ??= { images: [] }).images.push({ src: '', alt: '' }); })}>+ Aggiungi foto</button>}>
            {gallery.images.length === 0 && <p className="we-empty">Nessuna foto.</p>}
            {gallery.images.map((img, gi) => (
              <GalleryRow key={gi} img={img} canUpload={uploadsEnabled} upload={uploadPhoto}
                onSrc={(src) => edit((c) => { c.gallery!.images[gi].src = src; })}
                onAlt={(alt) => edit((c) => { c.gallery!.images[gi].alt = alt; })}
                onRemove={() => edit((c) => { c.gallery!.images.splice(gi, 1); })} />
            ))}
          </Section>

          <Section id="contatti" icon="mail" title="Contatti" desc="Dove trovarvi e come prenotare.">
            <Field label="Indirizzo (via e numero)">
              <input className="we-input" value={contact.address?.street ?? ''} placeholder="Via e numero civico"
                onChange={(e) => edit((c) => { ((c.contact ??= {}).address ??= { street: '', city: '', postalCode: '' }).street = e.target.value; })} />
            </Field>
            <Row>
              <Field label="Città"><input className="we-input" value={contact.address?.city ?? ''} placeholder="Città"
                onChange={(e) => edit((c) => { ((c.contact ??= {}).address ??= { street: '', city: '', postalCode: '' }).city = e.target.value; })} /></Field>
              <Field label="CAP"><input className="we-input" value={contact.address?.postalCode ?? ''} placeholder="CAP"
                onChange={(e) => edit((c) => { ((c.contact ??= {}).address ??= { street: '', city: '', postalCode: '' }).postalCode = e.target.value; })} /></Field>
              <Field label="Provincia"><input className="we-input" value={contact.address?.region ?? ''} placeholder="Prov."
                onChange={(e) => edit((c) => { ((c.contact ??= {}).address ??= { street: '', city: '', postalCode: '' }).region = e.target.value || undefined; })} /></Field>
            </Row>
            <Row>
              <Field label="Telefono"><input className="we-input" value={contact.phone ?? ''} placeholder="+39 …"
                onChange={(e) => edit((c) => { (c.contact ??= {}).phone = e.target.value || undefined; })} /></Field>
              <Field label="Email"><input className="we-input" value={contact.email ?? ''} placeholder="tavoli@…"
                onChange={(e) => edit((c) => { (c.contact ??= {}).email = e.target.value || undefined; })} /></Field>
              <Field label="WhatsApp"><input className="we-input" value={contact.whatsapp ?? ''} placeholder="+39…"
                onChange={(e) => edit((c) => { (c.contact ??= {}).whatsapp = e.target.value || undefined; })} /></Field>
            </Row>
            <Field label="Link indicazioni (Google Maps)">
              <input className="we-input" value={contact.map?.directionsUrl ?? ''} placeholder="https://maps.google.com/?q=…"
                onChange={(e) => edit((c) => { ((c.contact ??= {}).map ??= {}).directionsUrl = e.target.value || undefined; })} />
            </Field>
          </Section>

          <Section id="social" icon="share" title="Social" desc="Solo i profili che inserisci appaiono nel sito.">
            <Row>
              <Field label="Instagram"><input className="we-input" value={social.instagram ?? ''} placeholder="https://instagram.com/…"
                onChange={(e) => edit((c) => { (c.social ??= {}).instagram = e.target.value || undefined; })} /></Field>
              <Field label="Facebook"><input className="we-input" value={social.facebook ?? ''} placeholder="https://facebook.com/…"
                onChange={(e) => edit((c) => { (c.social ??= {}).facebook = e.target.value || undefined; })} /></Field>
            </Row>
            <Row>
              <Field label="TripAdvisor"><input className="we-input" value={social.tripadvisor ?? ''} placeholder="https://tripadvisor.com/…"
                onChange={(e) => edit((c) => { (c.social ??= {}).tripadvisor = e.target.value || undefined; })} /></Field>
              <Field label="Google"><input className="we-input" value={social.googleBusiness ?? ''} placeholder="https://g.page/…"
                onChange={(e) => edit((c) => { (c.social ??= {}).googleBusiness = e.target.value || undefined; })} /></Field>
            </Row>
          </Section>

          <Section id="seo" icon="search" title="Google & SEO" desc="Come appare il sito su Google e quando lo condividi.">
            <Field label="Titolo">
              <input className="we-input" value={seo.title ?? ''} placeholder="Nome del locale — cosa siete e dove"
                onChange={(e) => edit((c) => { (c.seo ??= {}).title = e.target.value || undefined; })} />
            </Field>
            <Field label="Descrizione" hint="~1-2 frasi. Appare sotto al titolo nei risultati di ricerca.">
              <textarea className="we-input we-textarea" rows={2} value={seo.description ?? ''} placeholder="Una frase che descrive il locale per Google…"
                onChange={(e) => edit((c) => { (c.seo ??= {}).description = e.target.value || undefined; })} />
            </Field>
          </Section>
        </div>

        {previewUrl && (
          <aside className="we-preview">
            <div className="we-preview-bar">
              <span>Anteprima dal vivo</span>
              <a
                href={previewUrl}
                target="risma-preview-full"
                onClick={(e) => {
                  e.preventDefault();
                  openFullPreview();
                }}
              >
                Apri ↗
              </a>
            </div>
            <div className="we-preview-stage">
              <iframe ref={iframeRef} src={previewUrl} title="Anteprima del sito" className="we-preview-frame" />
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function ensureDay(c: SiteContent, day: SiteWeekday) {
  const h = (c.hours ??= { timezone: 'Europe/Rome', week: [] });
  let row = h.week.find((x) => x.day === day);
  if (!row) { row = { day, ranges: [] }; h.week.push(row); }
  row.ranges ??= [];
  return row;
}

// ── presentational helpers ──
function Section({ id, icon, title, desc, action, children }: {
  id: string; icon: SecName; title: string; desc?: string; action?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <section className="we-card" id={'we-sec-' + id}>
      <header className="we-card-head">
        <span className="we-card-ico"><SecIcon name={icon} /></span>
        <div className="we-card-head-t">
          <h3>{title}</h3>
          {desc && <p className="we-muted">{desc}</p>}
        </div>
        {action && <div className="we-card-action">{action}</div>}
      </header>
      <div className="we-card-body">{children}</div>
    </section>
  );
}
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="we-field">
      <span className="we-label">{label}</span>
      {hint && <span className="we-hint">{hint}</span>}
      {children}
    </div>
  );
}
function Row({ children }: { children: React.ReactNode }) {
  return <div className="we-row">{children}</div>;
}
// Shared file-picker logic: a hidden <input>, a busy flag, and an error flag.
// `onUrl` receives the uploaded photo's public URL.
function usePhotoPicker(upload: (f: File) => Promise<string>, onUrl: (url: string) => void) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setErr(false);
    setBusy(true);
    try {
      onUrl(await upload(file));
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  }
  return { inputRef, busy, err, onFile, pick: () => inputRef.current?.click() };
}

const ACCEPT = 'image/jpeg,image/png,image/webp';

const PhotoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9.5" r="1.5" /><path d="M21 16l-5-5L5 20" />
  </svg>
);

/** Foto compatta per singolo piatto (layout menu "Con foto", #39). */
function DishPhoto({ img, canUpload, upload, onChange }: {
  img?: { src: string; alt: string };
  canUpload: boolean;
  upload: (file: File) => Promise<string>;
  onChange: (img: { src: string; alt: string } | undefined) => void;
}) {
  const setSrc = (src: string) => onChange(src ? { src, alt: img?.alt ?? '' } : undefined);
  const { inputRef, busy, onFile, pick } = usePhotoPicker(upload, setSrc);
  if (!canUpload && !img?.src) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
      <button
        type="button"
        className={'we-thumb' + (canUpload ? ' we-thumb--btn' : '')}
        style={{ width: 46, height: 46, ...(img?.src ? { backgroundImage: `url(${img.src})` } : {}) }}
        onClick={canUpload ? pick : undefined}
        disabled={!canUpload || busy}
        title="Foto del piatto"
        aria-label="Foto del piatto"
      >
        {busy ? <span className="we-spin" aria-hidden="true" /> : !img?.src && <PhotoIcon />}
      </button>
      {canUpload && (
        <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', fontSize: 12.5 }}>
          <button type="button" className="we-btn we-btn-soft" onClick={pick} disabled={busy}>
            {busy ? 'Caricamento…' : img?.src ? 'Cambia foto' : 'Foto del piatto'}
          </button>
          {img?.src && !busy && <button type="button" className="we-link-rm" onClick={() => onChange(undefined)}>Rimuovi</button>}
        </span>
      )}
      <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={onFile} />
    </div>
  );
}

function ImageField({ label, hint, img, onChange, canUpload, upload }: {
  label: string; hint?: string; img?: { src: string; alt: string };
  onChange: (img: { src: string; alt: string } | undefined) => void;
  canUpload: boolean;
  upload: (file: File) => Promise<string>;
}) {
  const setSrc = (src: string) => onChange(src ? { src, alt: img?.alt ?? '' } : undefined);
  const { inputRef, busy, err, onFile, pick } = usePhotoPicker(upload, setSrc);
  return (
    <div className="we-field">
      <span className="we-label">{label}</span>
      {hint && <span className="we-hint">{hint}</span>}
      <div className="we-imgfield">
        <button
          type="button"
          className={'we-thumb' + (canUpload ? ' we-thumb--btn' : '')}
          style={img?.src ? { backgroundImage: `url(${img.src})` } : undefined}
          onClick={canUpload ? pick : undefined}
          disabled={!canUpload || busy}
          aria-label={canUpload ? 'Carica foto' : undefined}
        >
          {busy ? <span className="we-spin" aria-hidden="true" /> : !img?.src && <PhotoIcon />}
        </button>
        <div className="we-imgfield-inputs">
          {canUpload && (
            <div className="we-upload-row">
              <button type="button" className="we-btn we-btn-soft" onClick={pick} disabled={busy}>
                {busy ? 'Caricamento…' : img?.src ? 'Cambia foto' : 'Carica foto'}
              </button>
              {img?.src && !busy && (
                <button type="button" className="we-link-rm" onClick={() => onChange(undefined)}>Rimuovi</button>
              )}
              {err && <span className="we-upload-err">Caricamento non riuscito</span>}
              <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={onFile} />
            </div>
          )}
          <input className="we-input" value={img?.src ?? ''} placeholder={canUpload ? 'oppure incolla un link…' : 'https://…/foto.jpg'}
            onChange={(e) => setSrc(e.target.value)} />
          <input className="we-input" value={img?.alt ?? ''} placeholder="Descrizione foto (per accessibilità)"
            onChange={(e) => onChange(img?.src ? { src: img.src, alt: e.target.value } : undefined)} />
        </div>
      </div>
    </div>
  );
}

// One row of the gallery: thumbnail + URL/alt inputs + upload + remove.
function GalleryRow({ img, canUpload, upload, onSrc, onAlt, onRemove }: {
  img: { src: string; alt: string };
  canUpload: boolean;
  upload: (file: File) => Promise<string>;
  onSrc: (src: string) => void;
  onAlt: (alt: string) => void;
  onRemove: () => void;
}) {
  const { inputRef, busy, err, onFile, pick } = usePhotoPicker(upload, onSrc);
  return (
    <div className="we-listrow we-galrow">
      <span className="we-galthumb" style={img.src ? { backgroundImage: `url(${img.src})` } : undefined} aria-hidden="true">
        {!img.src && !busy && <PhotoIcon />}
        {busy && <span className="we-spin" aria-hidden="true" />}
      </span>
      <div className="we-galrow-inputs">
        <input className="we-input" value={img.src} placeholder={canUpload ? 'Carica o incolla un link…' : 'https://…/foto.jpg'}
          onChange={(e) => onSrc(e.target.value)} />
        <input className="we-input" value={img.alt} placeholder="Descrizione"
          onChange={(e) => onAlt(e.target.value)} />
        {err && <span className="we-upload-err">Caricamento non riuscito</span>}
      </div>
      {canUpload && (
        <>
          <button type="button" className="we-btn we-btn-soft" onClick={pick} disabled={busy}>
            {busy ? '…' : img.src ? 'Cambia' : 'Carica'}
          </button>
          <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={onFile} />
        </>
      )}
      <button className="we-link-rm" onClick={onRemove}>Rimuovi</button>
    </div>
  );
}

// ── tiny section icon set ──
type SecName = 'star' | 'book' | 'grid' | 'clock' | 'image' | 'mail' | 'share' | 'search';
function SecIcon({ name }: { name: SecName }) {
  const P: Record<SecName, string> = {
    star: '<path d="M12 3l2.6 5.6 6 .8-4.4 4.1 1.1 6L12 16.9 6.7 19.6l1.1-6L3.4 9.4l6-.8L12 3z"/>',
    book: '<path d="M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2V4z"/><path d="M5 16h13"/>',
    grid: '<rect x="4" y="4" width="7" height="7" rx="1"/><rect x="13" y="4" width="7" height="7" rx="1"/><rect x="4" y="13" width="7" height="7" rx="1"/><rect x="13" y="13" width="7" height="7" rx="1"/>',
    clock: '<circle cx="12" cy="12" r="8"/><path d="M12 8v4l3 2"/>',
    image: '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="M21 16l-5-5L5 20"/>',
    mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
    share: '<circle cx="6" cy="12" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="18" cy="18" r="2.5"/><path d="M8.2 10.8 15.8 7M8.2 13.2l7.6 3.8"/>',
    search: '<circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>',
  };
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{ __html: P[name] }} />
  );
}
